import { Screens } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import React from 'react';
import {
  selectAseExplorerOptionSize,
  selectAseFieldInstanceAst,
  selectAseIsEditable,
  selectAseResultFieldInstanceCheck,
  selectAseTplFieldInstanceCheck,
  selectAseTplFieldInstanceDefaultChecksForGroup,
} from 'store/assessments/selectors';
import { CardRow } from 'components/CardRow';
import { numberToDate } from 'lib/riskbase';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useDispatch } from 'react-redux';
import { doAseOpenInstanceCheck } from 'store/assessments/actions';
import {
  AseFieldInstanceCheckStatusEnum,
  AseFieldInstanceStatuses,
  FieldInstanceCheckScheduleStatusEnum,
} from 'store/assessments/types';
import { formatDate } from 'date-fns';
import { theme } from 'lib/theme';
import { TxtPreset, Txt } from 'components/Txt';
import { View } from 'react-native';
import { UI_DATE_FORMAT_SHORT } from 'lib/constants';
import { InstanceCheckAvailabilityBadge } from '../Parts/InstanceCheckAvailabilityBadge';

export const ExplorerInstanceCheckRow: React.FC<{
  id: string;
  instanceId: string;
}> = ({ id, instanceId }) => {
  const dispatch = useDispatch();
  const isEditable = useSelector(selectAseIsEditable);
  const explorerOptionSize = useSelector(selectAseExplorerOptionSize);

  const check = useSelector((state) =>
    selectAseTplFieldInstanceCheck(state, id),
  );
  const result = useSelector((state) =>
    selectAseResultFieldInstanceCheck(state, id, instanceId),
  );
  const instance = useSelector((state) =>
    selectAseFieldInstanceAst(state, instanceId),
  );
  const actualCheckData = instance?.checks?.[id];
  const defaultCheckData = useSelector((state) =>
    selectAseTplFieldInstanceDefaultChecksForGroup(
      state,
      instance?.groupId || '-1',
    ),
  )[id];

  if (!check) {
    return null;
  }
  const checkData = actualCheckData || defaultCheckData;
  const status =
    AseFieldInstanceStatuses[
      result?.statusId || AseFieldInstanceCheckStatusEnum.completed
    ];
  const isRequired =
    checkData?.schedule.statusId !==
    FieldInstanceCheckScheduleStatusEnum.completed;

  return (
    <>
      <SeparatorHorizontal />
      <CardRow
        navigationIsSelected={{
          name: Screens.AseInstanceCheck,
          params: { id: check.id, instanceId },
        }}
        onPress={() =>
          dispatch(doAseOpenInstanceCheck({ id: check.id, instanceId }))
        }
        icon={status.icon}
        iconColor={status.color}
        labelAlignTop
        compact={explorerOptionSize === 'small'}
        small={explorerOptionSize === 'small'}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.paddingHorizontalHalf,
          }}
        >
          <View style={{ flex: 1 }}>
            <Txt preset={TxtPreset.CardRowLabel}>{check.name}</Txt>

            {!isEditable ? undefined : (
              <Txt
                size="xsmall"
                style={{
                  color: theme.colors.fieldValueText,
                }}
              >
                <Txt size="xsmall">
                  {isRequired ? 'Required' : 'Not Required'}
                </Txt>
                &nbsp;&bull;&nbsp;
                <Txt size="xsmall">
                  Previous:{' '}
                  <Txt size="xsmall">
                    {checkData?.lastCompleted
                      ? formatDate(
                          numberToDate(checkData?.lastCompleted.date),
                          UI_DATE_FORMAT_SHORT,
                        )
                      : 'Never'}
                  </Txt>
                  {checkData?.lastAttempted && (
                    <Txt size="xsmall">
                      &nbsp;&bull;&nbsp;Attempted:{' '}
                      <Txt size="xsmall">
                        {formatDate(
                          numberToDate(checkData?.lastAttempted.date),
                          UI_DATE_FORMAT_SHORT,
                        )}
                      </Txt>
                    </Txt>
                  )}
                </Txt>
              </Txt>
            )}
          </View>
          <InstanceCheckAvailabilityBadge
            availability={checkData?.availability || []}
          />
        </View>
      </CardRow>
    </>
  );
};
